import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeInUp, staggerItem } from '../utils/animations';
import { useTheme, getThemeColors } from '../contexts/ThemeContext';
import Section from './Section';
import Card from './Card';

const Sectors = () => {
  const { isDarkMode } = useTheme();
  const colors = getThemeColors(isDarkMode);
  const [isCirclesFixed, setIsCirclesFixed] = useState(false);
  const sectionRef = React.useRef(null);
  const circlesRef = React.useRef(null);
  
  const sectors = [
    {
      id: 'construction',
      title: 'Construction',
      subtitle: 'Building the Future',
      description: 'High-quality reinforcement steel for residential, commercial, and infrastructure construction projects.',
      image: '/api/placeholder/600/400',
      stats: [
        { label: 'Projects Served', value: '2500+' },
        { label: 'Rebar Supplied', value: '150K+ tons' },
        { label: 'Client Satisfaction', value: '98.5%' }
      ],
      applications: [
        'High-rise buildings',
        'Residential complexes',
        'Shopping centers',
        'Industrial facilities'
      ],
      keyRequirements: [
        'ASTM A615 Grade 60 compliance',
        'Consistent mechanical properties',
        'Reliable delivery schedules',
        'Competitive pricing'
      ]
    },
    {
      id: 'infrastructure',
      title: 'Infrastructure',
      subtitle: 'Foundation of Progress',
      description: 'Specialized steel products for bridges, roads, airports, and other critical infrastructure projects.',
      image: '/api/placeholder/600/400',
      stats: [
        { label: 'Major Projects', value: '150+' },
        { label: 'Bridge Construction', value: '25 projects' },
        { label: 'Road Projects', value: '75+ km' }
      ],
      applications: [
        'Bridge construction',
        'Highway development',
        'Airport infrastructure',
        'Port facilities'
      ],
      keyRequirements: [
        'High-strength specifications',
        'Seismic resistance properties',
        'Long-term durability',
        'Structural integrity'
      ]
    },
    {
      id: 'fabrication',
      title: 'Steel Fabrication',
      subtitle: 'Precision Manufacturing',
      description: 'Quality steel bars and sections for fabrication shops and manufacturing industries.',
      image: '/api/placeholder/600/400',
      stats: [
        { label: 'Fabrication Partners', value: '200+' },
        { label: 'Custom Products', value: '50+ types' },
        { label: 'Quality Rating', value: '99.2%' }
      ],
      applications: [
        'Structural fabrication',
        'Machine components',
        'Equipment manufacturing',
        'Industrial frameworks'
      ],
      keyRequirements: [
        'Precise dimensional control',
        'Surface quality standards',
        'Machinability properties',
        'Consistent chemistry'
      ]
    },
    {
      id: 'retail',
      title: 'Retail Distribution',
      subtitle: 'Direct to Market',
      description: 'Comprehensive steel product range for hardware stores, distributors, and retail partners.',
      image: '/api/placeholder/600/400',
      stats: [
        { label: 'Retail Partners', value: '500+' },
        { label: 'Distribution Centers', value: '12 locations' },
        { label: 'Product Varieties', value: '100+ SKUs' }
      ],
      applications: [
        'Hardware retail',
        'Construction supplies',
        'DIY markets',
        'Regional distribution'
      ],
      keyRequirements: [
        'Standard size availability',
        'Competitive pricing',
        'Flexible order quantities',
        'Fast delivery'
      ]
    }
  ];

  const [activeSector, setActiveSector] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);

  // Handle sticky circles behavior
  React.useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !circlesRef.current) return;
      
      const section = sectionRef.current;
      const sectionRect = section.getBoundingClientRect();
      
      // Check if section is in viewport
      const sectionInView = sectionRect.top < window.innerHeight && sectionRect.bottom > 0;
      
      // Check if circles should be fixed (when section top goes above viewport)
      const shouldBeFixed = sectionRect.top < 16 && sectionInView;
      
      setIsCirclesFixed(shouldBeFixed);
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    handleScroll();
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!autoRotate) return;
    
    const interval = setInterval(() => {
      setActiveSector((prev) => (prev + 1) % sectors.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRotate, sectors.length]);

  const currentSector = sectors[activeSector];

  return (
    <Section id="sectors" background="gradient" className="relative overflow-hidden" ref={sectionRef}>
      {/* Section Header */}
      <motion.div
        className="text-center mb-16"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <h2 className={`text-h2 font-bold ${colors.text.primary} mb-6`}>
          Industries We <span className="text-primary-400">Serve</span>
        </h2>
        <p className={`text-body-lg ${colors.text.secondary} max-w-3xl mx-auto leading-relaxed`}>
          From major infrastructure projects to specialized fabrication needs, 
          we deliver steel solutions across diverse industry sectors.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Desktop Sector Navigation - Hidden on mobile */}
        <motion.div
          className="hidden lg:block space-y-4"
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          {sectors.map((sector, index) => (
            <motion.button
              key={sector.id}
              onClick={() => {
                setActiveSector(index);
                setAutoRotate(false);
              }}
              className={`w-full text-left p-6 rounded-xl transition-all duration-500 ${
                index === activeSector
                  ? 'bg-primary-400 text-black shadow-magnetic'
                  : `${colors.bg.muted}/50 ${colors.text.secondary} hover:${colors.bg.card}/50 hover:${colors.text.primary}`
              }`}
              whileHover={{ x: 10 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className={`text-xl font-semibold ${
                  index === activeSector ? 'text-black' : colors.text.primary
                }`}>
                  {sector.title}
                </h3>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  index === activeSector 
                    ? 'bg-black text-primary-400' 
                    : 'bg-primary-400 text-black'
                }`}>
                  {index + 1}
                </div>
              </div>
              <p className={`text-sm ${
                index === activeSector ? 'text-neutral-800' : colors.text.muted
              }`}>
                {sector.subtitle}
              </p>
              
              {/* Progress bar for active sector */}
              {index === activeSector && autoRotate && (
                <motion.div
                  className="mt-4 h-1 bg-black/20 rounded-full overflow-hidden"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <motion.div
                    className="h-full bg-black rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 5, ease: 'linear' }}
                  />
                </motion.div>
              )}
            </motion.button>
          ))}
        </motion.div>

        {/* Mobile Circular Navigation - Only visible on mobile */}
        <div className="lg:hidden">
          {/* Placeholder when circles are fixed to maintain layout */}
          {isCirclesFixed && <div className="mb-16"></div>}
          
          {/* Dynamic Positioning Container */}
          <div 
            ref={circlesRef}
            className={`${
              isCirclesFixed 
                ? 'fixed left-1/2 transform -translate-x-1/2 z-40' 
                : 'relative mb-8'
            } transition-all duration-500 ease-out`}
            style={isCirclesFixed ? { 
              top: '60px',
              background: 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              padding: '8px 16px',
              border: '1px solid rgba(247, 148, 29, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(247, 148, 29, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            } : {}}
          >
            <motion.div
              className="flex justify-center items-center"
              variants={fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className={`flex items-center ${isCirclesFixed ? 'space-x-3' : 'space-x-8'} transition-all duration-300`}>
                {sectors.map((sector, index) => (
                  <motion.button
                    key={sector.id}
                    onClick={() => {
                      setActiveSector(index);
                      setAutoRotate(false);
                    }}
                    className="relative flex flex-col items-center"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    animate={{
                      scale: isCirclesFixed ? 0.7 : 1,
                      transition: { duration: 0.3 }
                    }}
                  >
                    {/* Circular Progress Background */}
                    <div className={`relative ${isCirclesFixed ? 'w-10 h-10' : 'w-12 h-12'} transition-all duration-300`}>
                      {/* Background Circle */}
                      <svg className={`${isCirclesFixed ? 'w-10 h-10' : 'w-12 h-12'} transform -rotate-90 transition-all duration-300`} viewBox="0 0 48 48">
                        <circle
                          cx="24"
                          cy="24"
                          r="20"
                          stroke={colors.bg.muted}
                          strokeWidth="3"
                          fill="none"
                        />
                        {/* Progress Circle */}
                        {index === activeSector && autoRotate && (
                          <motion.circle
                            cx="24"
                            cy="24"
                            r="20"
                            stroke="#f7941d"
                            strokeWidth="3"
                            fill="none"
                            strokeLinecap="round"
                            strokeDasharray={`${2 * Math.PI * 20}`}
                            initial={{ strokeDashoffset: 2 * Math.PI * 20 }}
                            animate={{ strokeDashoffset: 0 }}
                            transition={{ duration: 5, ease: 'linear' }}
                          />
                        )}
                        {/* Completed Circle for past sectors */}
                        {index < activeSector && (
                          <circle
                            cx="24"
                            cy="24"
                            r="20"
                            stroke="#f7941d"
                            strokeWidth="3"
                            fill="none"
                            strokeDasharray={`${2 * Math.PI * 20}`}
                            strokeDashoffset={0}
                          />
                        )}
                      </svg>
                      
                      {/* Number in Center */}
                      <div className={`absolute inset-0 flex items-center justify-center ${isCirclesFixed ? 'w-10 h-10' : 'w-12 h-12'} rounded-full ${isCirclesFixed ? 'text-xs' : 'text-sm'} font-bold transition-all duration-300 backdrop-blur-sm ${
                        index === activeSector
                          ? 'bg-primary-400 text-black'
                          : index < activeSector
                          ? 'bg-primary-400 text-black'
                          : `${colors.bg.muted} ${colors.text.secondary}`
                      }`}>
                        {index + 1}
                      </div>
                    </div>
                    
                    {/* Sector Title - Hidden when fixed */}
                    <AnimatePresence>
                      {!isCirclesFixed && (
                        <motion.span 
                          className={`mt-2 text-xs font-medium text-center transition-all duration-300 ${
                            index === activeSector ? 'text-primary-400' : colors.text.muted
                          }`}
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          transition={{ delay: 0.1 }}
                        >
                          {sector.title}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Sector Details */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSector}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
            className="space-y-8"
          >
            {/* Main Card */}
            <Card className="p-8 relative overflow-hidden">
              <div className="absolute top-4 right-4 w-16 h-1 bg-primary-400"></div>
              
              <div className="relative z-10">
                <h3 className={`text-2xl font-bold ${colors.text.primary} mb-2`}>{currentSector.title}</h3>
                <p className="text-primary-400 font-medium mb-4">{currentSector.subtitle}</p>
                <p className={`${colors.text.secondary} leading-relaxed mb-6`}>
                  {currentSector.description}
                </p>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {currentSector.stats.map((stat, index) => (
                    <motion.div
                      key={stat.label}
                      className="text-center"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="text-2xl font-bold text-primary-400 mb-1">
                        {stat.value}
                      </div>
                      <div className={`${colors.text.muted} text-xs`}>
                        {stat.label}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Applications & Requirements */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Applications */}
              <Card glass={true} className="p-6">
                <h4 className="${colors.text.primary} font-semibold mb-4 flex items-center">
                  <svg className="w-5 h-5 text-primary-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Applications
                </h4>
                <ul className="space-y-2">
                  {currentSector.applications.map((app, index) => (
                    <motion.li
                      key={app}
                      className="flex items-center ${colors.text.secondary} text-sm"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="w-1.5 h-1.5 bg-primary-400 rounded-full mr-3"></div>
                      {app}
                    </motion.li>
                  ))}
                </ul>
              </Card>

              {/* Key Requirements */}
              <Card glass={true} className="p-6">
                <h4 className="${colors.text.primary} font-semibold mb-4 flex items-center">
                  <svg className="w-5 h-5 text-primary-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Key Requirements
                </h4>
                <ul className="space-y-2">
                  {currentSector.keyRequirements.map((req, index) => (
                    <motion.li
                      key={req}
                      className="flex items-center ${colors.text.secondary} text-sm"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="w-1.5 h-1.5 bg-primary-400 rounded-full mr-3"></div>
                      {req}
                    </motion.li>
                  ))}
                </ul>
              </Card>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Auto-rotate Control */}
      <motion.div
        className="flex justify-center mt-12"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        transition={{ delay: 0.8 }}
      >
        <button
          onClick={() => setAutoRotate(!autoRotate)}
          className={`px-6 py-3 rounded-xl transition-all duration-300 flex items-center space-x-3 ${
            autoRotate
              ? 'bg-primary-400 text-black'
              : `${colors.bg.muted} ${colors.text.secondary} hover:${colors.bg.card}`
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {autoRotate ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M19 4h1a1 1 0 011 1v1M5 4H4a1 1 0 00-1 1v1m0 4v6m20-6v6m0 4v1a1 1 0 01-1 1h-1m-6 0H9m-4 0H4a1 1 0 01-1-1v-1" />
            )}
          </svg>
          <span>{autoRotate ? 'Pause' : 'Play'} Auto-rotation</span>
        </button>
      </motion.div>
    </Section>
  );
};

export default Sectors;